// Full Row Detail: aggregated sheet columns.
//
// On the Excel Layout tab each Full Row Detail column can carry an Aggregate
// (ExcelDetailColumnOptions.aggregate): Sum, Average, Minimum, Maximum, Count or Count
// Distinct. As soon as one INCLUDED column has one, the detail query becomes a grouped query -
// the aggregated columns are worked out per group of every other included column:
//
//     SELECT TripStatus, Vendor, SUM(BilledAmount), COUNT(TripId) ... GROUP BY TripStatus, Vendor
//
// Rules - mirrored by ReportSqlBuilder.DetailAggregates.cs, keep the two identical:
//   - Columns not included on the sheet are left out of the query (they would split the
//     groups). Fixed value columns are selected but never grouped by (same on every row).
//   - Sum / Average read the value as DECIMAL(38, 6) (TRY_CONVERT); Minimum / Maximum of a date
//     compare dates (a text-backed date is converted first); Count counts non-blank values,
//     Count Distinct different non-blank values.
//   - Numbers take all six; dates Minimum, Maximum, Count, Count Distinct; text Count and Count
//     Distinct; fixed values none. A numeric lookup code counts as text.
//   - A metric that shows a column from a related table repeats each record once per related
//     row, so Sum, Average and Count are refused there (as for metric measures).
//   - A subquery (a text calculated field) is computed in a CROSS APPLY - SQL Server refuses it
//     in GROUP BY and inside an aggregate.
//   - Grand-total row (layout.detailTotals.show): each aggregated column also returns its total
//     over all groups as a window over the aggregate ("detailTotal:<alias>"): Sum and Count add
//     up, Minimum / Maximum take the overall extreme, Average is recalculated from the
//     underlying sum and count. Count Distinct has no total (a value can sit in several groups).
//   - "Unique rows only" does nothing while grouping.
import type { CalculatedValueType, DataColumn, DetailAggregate, ExcelCellFormat, ExcelColumnLayout, MetricSection, ReportConfig, SectionExcelLayout } from "../types";
import type { ReportLogicContext } from "./context";
import { columnMatchesKey, detailColumnsForModel } from "./catalogResolution";
import { calculatedFieldValueType, findCalculatedField, DETAIL_CALC_ALIAS_PREFIX } from "./calculatedFields";
import { columnDataTypeCategory } from "./operatorCatalog";
import { isRelatedColumn } from "./relatedTables";

export const DETAIL_TOTAL_ALIAS_PREFIX = "detailTotal:";
const CALC_DECIMAL = "DECIMAL(38, 6)";
const LOOKUP_CODE_NUMERIC_TYPES = ["int", "bigint", "smallint", "tinyint", "decimal", "numeric"];

export type DetailOutputValueType = CalculatedValueType | "constant";

export const DETAIL_AGGREGATES: { key: DetailAggregate; label: string; headerPrefix: string; hint: string }[] = [
    { key: "none", label: "None (Group By)", headerPrefix: "", hint: "Shows the value; rows are grouped by it" },
    { key: "sum", label: "Sum", headerPrefix: "Sum of", hint: "Adds the values up" },
    { key: "avg", label: "Average", headerPrefix: "Average", hint: "Average of the values" },
    { key: "min", label: "Minimum", headerPrefix: "Minimum", hint: "Smallest value (earliest date)" },
    { key: "max", label: "Maximum", headerPrefix: "Maximum", hint: "Largest value (latest date)" },
    { key: "count", label: "Count", headerPrefix: "Count of", hint: "How many non-blank values" },
    { key: "countDistinct", label: "Count Distinct", headerPrefix: "Distinct", hint: "How many different non-blank values" },
];

export function detailAggregateDef(key: DetailAggregate | undefined | null) {
    return DETAIL_AGGREGATES.find((item) => item.key === (key || "none")) || DETAIL_AGGREGATES[0];
}

/** The default header of an aggregated column, e.g. "Sum of Billed Amount". */
export function detailAggregateHeader(aggregate: DetailAggregate | undefined, label: string) {
    const def = detailAggregateDef(aggregate);
    return def.key === "none" ? label : `${def.headerPrefix} ${label}`;
}

export function detailTotalAlias(sourceAlias: string) {
    return `${DETAIL_TOTAL_ALIAS_PREFIX}${sourceAlias}`;
}

// --- Columns ----------------------------------------------------------------------------

function catalogValueType(column: DataColumn): DetailOutputValueType {
    const category = columnDataTypeCategory(column);
    if (category === "numeric") {
        const dataType = String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
        return column.lookupSourceId && LOOKUP_CODE_NUMERIC_TYPES.includes(dataType) ? "text" : "number";
    }
    return category === "date" ? "date" : "text";
}

/** The catalog column behind a "detail:<selection key>" alias, if it is one. */
export function detailCatalogColumn(context: ReportLogicContext, section: MetricSection, sourceAlias: string): DataColumn | null {
    if (!sourceAlias.startsWith("detail:") || sourceAlias.startsWith(DETAIL_CALC_ALIAS_PREFIX)
        || sourceAlias.startsWith("detail:ruleSet:") || sourceAlias.startsWith("detail:const:")) return null;
    const key = sourceAlias.slice("detail:".length);
    return detailColumnsForModel(context, section.model).find((column) => columnMatchesKey(column, key)) || null;
}

/** What a detail sheet column holds, which decides the aggregates it offers. Mirrors DetailOutput.ValueType. */
export function detailOutputValueType(context: ReportLogicContext, report: ReportConfig | null | undefined, section: MetricSection, sourceAlias: string): DetailOutputValueType {
    if (sourceAlias.startsWith("detail:const:")) return "constant";
    if (sourceAlias.startsWith("detail:ruleSet:")) return "text";
    if (sourceAlias.startsWith(DETAIL_CALC_ALIAS_PREFIX)) {
        return calculatedFieldValueType(findCalculatedField(report, sourceAlias.slice(DETAIL_CALC_ALIAS_PREFIX.length)));
    }
    const column = detailCatalogColumn(context, section, sourceAlias);
    // A related column is joined row by row in a detail query, so it keeps its own type (the
    // fanned-rows rule in detailAggregateIssues still applies).
    return column ? catalogValueType(column) : "text";
}

/** The aggregates a column of this type can take ("none" always first). */
export function allowedDetailAggregates(valueType: DetailOutputValueType): DetailAggregate[] {
    switch (valueType) {
        case "number": return ["none", "sum", "avg", "min", "max", "count", "countDistinct"];
        case "date": return ["none", "min", "max", "count", "countDistinct"];
        case "text": return ["none", "count", "countDistinct"];
        default: return ["none"];
    }
}

export function detailAggregateOf(column: ExcelColumnLayout | undefined | null): DetailAggregate {
    const value = column?.detail?.aggregate;
    return value && value !== "none" ? value : "none";
}

/** A column missing from the layout (not reconciled yet) counts as included. */
export function isDetailColumnOnSheet(layout: SectionExcelLayout | undefined, sourceAlias: string) {
    const column = layout?.columns.find((item) => item.sourceAlias === sourceAlias);
    return column?.detail?.include ?? true;
}

/** The detail layout whose aggregates shape the query, when the metric is Full Row Detail. */
export function detailAggregateLayout(section: MetricSection) {
    return section.displayAs === "detail" && section.excelLayout?.mode === "detail" ? section.excelLayout : undefined;
}

/** True when an included column of the metric's detail layout is aggregated. */
export function usesDetailAggregates(section: MetricSection) {
    const layout = detailAggregateLayout(section);
    return !!layout?.columns.some((column) => (column.detail?.include ?? true) && detailAggregateOf(column) !== "none");
}

/** A sensible format for a freshly aggregated column (the user can still change it). */
export function defaultFormatForAggregate(aggregate: DetailAggregate, valueType: DetailOutputValueType, current: ExcelCellFormat): ExcelCellFormat {
    if (aggregate === "count" || aggregate === "countDistinct") return "integer";
    if (aggregate === "avg") return "decimal2";
    if (aggregate === "sum" && current === "text") return "decimal2";
    if ((aggregate === "min" || aggregate === "max") && valueType === "number" && current === "text") return "decimal2";
    return current;
}

// --- SQL --------------------------------------------------------------------------------

/**
 * The aggregate and its grand total for one column. `input` is the column's value as the
 * query reads it (already hoisted when it holds a subquery); `dateInput` the same converted to
 * a date. Mirrors the switch in BuildAggregatedDetailSql.
 */
export function detailAggregateSql(aggregate: DetailAggregate, valueType: DetailOutputValueType, input: string, dateInput: string, hoist: (sql: string) => string): { value: string; total: string | null } {
    // Hoisted only where used, so the CROSS APPLY holds exactly what the server's does.
    const number = () => hoist(`TRY_CONVERT(${CALC_DECIMAL}, ${input})`);
    switch (aggregate) {
        case "sum": {
            const value = `SUM(${number()})`;
            return { value, total: `SUM(${value}) OVER ()` };
        }
        case "avg": {
            const value = number();
            return { value: `AVG(${value})`, total: `SUM(SUM(${value})) OVER () / NULLIF(SUM(COUNT(${value})) OVER (), 0)` };
        }
        case "min": {
            const value = `MIN(${valueType === "number" ? number() : hoist(dateInput)})`;
            return { value, total: `MIN(${value}) OVER ()` };
        }
        case "max": {
            const value = `MAX(${valueType === "number" ? number() : hoist(dateInput)})`;
            return { value, total: `MAX(${value}) OVER ()` };
        }
        case "count": {
            const value = `COUNT(${hoist(input)})`;
            return { value, total: `SUM(${value}) OVER ()` };
        }
        case "countDistinct":
            return { value: `COUNT(DISTINCT ${hoist(input)})`, total: null };
        default:
            return { value: input, total: null };
    }
}

// --- Validation -------------------------------------------------------------------------

export interface DetailAggregateIssue {
    sourceAlias: string;
    message: string;
}

/** Why the metric's aggregated detail layout can't be built (empty when it can). */
export function detailAggregateIssues(context: ReportLogicContext, report: ReportConfig | null | undefined, section: MetricSection): DetailAggregateIssue[] {
    const layout = detailAggregateLayout(section);
    if (!layout || !usesDetailAggregates(section)) return [];
    const issues: DetailAggregateIssue[] = [];
    const included = layout.columns.filter((column) => column.detail?.include ?? true);
    // A related column on the sheet repeats each record per related row.
    const fanned = included.some((column) => {
        const catalogColumn = detailCatalogColumn(context, section, column.sourceAlias);
        return !!catalogColumn && isRelatedColumn(catalogColumn);
    });
    included.forEach((column) => {
        const aggregate = detailAggregateOf(column);
        if (aggregate === "none") return;
        const valueType = detailOutputValueType(context, report, section, column.sourceAlias);
        const name = column.detail?.header?.trim() || column.label;
        const def = detailAggregateDef(aggregate);
        if (!allowedDetailAggregates(valueType).includes(aggregate)) {
            issues.push({
                sourceAlias: column.sourceAlias,
                message: valueType === "constant"
                    ? `"${name}" is a fixed value column and can't be aggregated.`
                    : `"${def.label}" doesn't fit "${name}" (${valueType}). Sum and Average need a number; Minimum and Maximum a number or a date.`,
            });
        } else if (fanned && (aggregate === "sum" || aggregate === "avg" || aggregate === "count")) {
            issues.push({
                sourceAlias: column.sourceAlias,
                message: `"${def.label}" of "${name}" would count a record more than once: the sheet shows a column from a related table, which repeats each record per related row. Use Minimum, Maximum or Count Distinct, or take the related column off the sheet.`,
            });
        }
    });
    return issues;
}
